import React, { useEffect, useMemo, useState } from 'react'
import { Paper, Grid, Typography, TableCell, TableRow } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

import { ComplianceModal } from '../complianceModal/ComplianceModal'
import { EditComplianceModal } from '../editComplianceModal/EditComplianceModal'

import { useMutation, useQueryList, useVettingEventsAccess } from '@percent/admin-dashboard/common/hooks'
import { ErrorView, Table, PercentButton, CellStatus } from '@percent/admin-dashboard/common/components'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import styles from './History.module.scss'
import { HistoryProps } from './History.types'
import {
  SubmitVettingEventsPayload,
  VettingEvent,
  VettingEventScope,
  VettingStatus
} from '@percent/admin-dashboard/api/types'
import { ConfirmationModal, Flex, Modal } from '@percent/lemonade'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { APIErrorHandler } from '@percent/admin-dashboard/common/library/APIErrorHandler'

const columns = [
  { id: 'event', isSortable: false },
  { id: 'status', isSortable: false },
  { id: 'eventDate', isSortable: false },
  { id: 'createdBy', isSortable: false }
]

export function History({
  nonprofitName,
  organisationId,
  vettingEventsResults,
  refreshVettingEventsResults
}: HistoryProps) {
  const { t } = useTranslation()
  const resubmitFlagsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_RESUBMIT_FLAGS)
  const { adminService } = useServices()
  const toast = useToast()

  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<string>('')
  // Recording an event resolves the organisation's pending events for that scope and
  // updates its monitoring subscriptions, so every write here is `can_create`.
  const { canCreate } = useVettingEventsAccess()

  const [isComplianceModalOpened, setIsComplianceModalOpened] = useState(false)
  const [isEditComplianceModalOpened, setIsEditComplianceModalOpened] = useState(false)
  const [isResubmitFlagsModalOpened, setIsResubmitFlagsModalOpened] = useState(false)
  const [isResubmitFlagsModalDisabled, setIsResubmitFlagsModalDisabled] = useState(false)

  const mapVettingEventStatus = (status: VettingStatus) => {
    switch (status) {
      case VettingStatus.PASS:
        return VettingStatus.NO
      case VettingStatus.FAIL:
        return VettingStatus.YES
      default:
        return status
    }
  }

  const [
    { dataOrNull: vettingEventsHistory, totalResults, isLoading, errorMessageOrNull },
    { query, nextPage, previousPage, refresh }
  ] = useQueryList(adminService.getVettingEvents, { organisationId })

  const [{ isLoading: isResubmittingFlags }, { apiFunc: resubmitFlagsFunc }] = useMutation(
    adminService.submitVettingEvents,
    () => {
      refreshVettingEventsResults()
      refresh()
      setIsResubmitFlagsModalOpened(false)
      setIsResubmitFlagsModalDisabled(false)

      toast(t('toast.resubmitComplianceFlagsSuccess'), 'success')
    },
    error => {
      if (axios.isAxiosError(error)) {
        toast(APIErrorHandler(error?.response?.data?.error), 'error')
      }
    }
  )

  const [{ isLoading: isGettingVettingEventsResultsLoading }, { apiFunc: getVettingEventsResultsFunc }] = useMutation(
    adminService.getVettingEventsResults,
    async response => {
      const { results } = response.data.data
      const isVettingResultPendingReview = Object.values(results ?? {}).some(
        result => result.status === VettingStatus.PENDING_REVIEW
      )

      const requestPayload = Object.keys(results ?? {}).reduce<SubmitVettingEventsPayload>(
        (acc, key) => {
          const status = results?.[key].status

          if (status) {
            acc.flags.push({
              scope: key as VettingEventScope,
              status: mapVettingEventStatus(status)
            })
          }

          return acc
        },
        { flags: [] }
      )

      if (isVettingResultPendingReview || !requestPayload.flags.length) {
        setIsResubmitFlagsModalDisabled(true)

        return
      }

      await resubmitFlagsFunc({ organisationId, payload: requestPayload })
    },
    error => {
      if (axios.isAxiosError(error)) {
        toast(APIErrorHandler(error?.response?.data?.error), 'error')
      }
    }
  )

  const { results: vettingResults } = vettingEventsResults

  const isComplianceReviewRequired = useMemo(
    () =>
      (vettingResults &&
        Object.keys(vettingResults).some(
          key => vettingResults[key].status === null || vettingResults[key].status === 'pending_review'
        )) ||
      false,
    [vettingResults]
  )

  useEffect(() => {
    // Gated too, not just the buttons: this opens the modal on its own, so a caller who
    // can only read would land on a submit button that 403s.
    setIsComplianceModalOpened(isComplianceReviewRequired && canCreate)
  }, [isComplianceReviewRequired, canCreate])

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)

    const newQueryParams = {
      direction: isAsc ? 'DESC' : 'ASC',
      sort: 'name',
      completed: true
    }
    query(newQueryParams)
  }

  const refreshResults = () => {
    refreshVettingEventsResults()
    refresh()
  }

  if (errorMessageOrNull) {
    return <ErrorView errorMessage={errorMessageOrNull} />
  }

  const emptyTableActions = isComplianceReviewRequired && canCreate && (
    <PercentButton
      className={styles.actionsButton}
      title={`+ ${t('button.createVettingEvent')}`}
      onClick={() => setIsComplianceModalOpened(true)}
      alignment="alignRight"
    />
  )

  const getCreatedBy = (row: VettingEvent) => {
    if (row.createdBy) {
      return row.account?.fullName || row.account?.email
    }

    return t('typography.automatically')
  }

  // Resubmit builds its payload from the compliance results endpoint, not from the history list.
  // When there are no results with a status (e.g. no monitoring subscription defining required
  // scopes), there is nothing to resubmit, so keep the button disabled instead of no-opping on click.
  const hasResubmittableFlags = Object.values(vettingResults ?? {}).some(result => result.status)

  const isResubmitFlagsBtnDisabled =
    !vettingEventsHistory?.length ||
    vettingEventsHistory.some(row => row.status === VettingStatus.PENDING_REVIEW) ||
    !hasResubmittableFlags

  return (
    <Grid container spacing={0} component={Paper} elevation={0} className={styles.historyContainer}>
      <div className={styles.detailsHeader}>
        <Typography variant="h6">{t('typography.history')}</Typography>
        <Flex gap={24}>
          {resubmitFlagsFeatureFlag && canCreate && (
            <PercentButton
              title={t('button.resubmitFlags')}
              onClick={() => setIsResubmitFlagsModalOpened(true)}
              alignment="alignRight"
              disabled={isResubmitFlagsBtnDisabled}
            />
          )}
          {canCreate && (
            <PercentButton
              title={
                isComplianceReviewRequired ? `+ ${t('button.createVettingEvent')}` : t('button.editComplianceEvent')
              }
              onClick={() => {
                isComplianceReviewRequired ? setIsComplianceModalOpened(true) : setIsEditComplianceModalOpened(true)
              }}
              alignment="alignRight"
            />
          )}
        </Flex>
      </div>
      <Table
        isLoading={isLoading}
        totalResults={totalResults}
        data={vettingEventsHistory}
        previousPage={previousPage}
        nextPage={nextPage}
        value={0}
        handleRequestSort={handleRequestSort}
        order={order}
        orderBy={orderBy}
        columns={columns}
        emptyTableText={`${nonprofitName} ${t('table.emptyVettingEvents')}`}
        emptyTableImageType="vettingEvents"
        emptyTableChildren={emptyTableActions}
        className={styles.historyTable}
        testId="compliance-history-table"
      >
        {vettingEventsHistory?.map(row => (
          <TableRow key={row.id} data-testid={`${row.scope}-history-row`}>
            <TableCell>{row.scope}</TableCell>
            <TableCell>
              <CellStatus status={row.result?.status} vettingStatus={row.status} />
            </TableCell>
            <TableCell>{getDate(row.createdAt)}</TableCell>
            <TableCell>{getCreatedBy(row)}</TableCell>
          </TableRow>
        ))}
      </Table>
      {isComplianceModalOpened && (
        <ComplianceModal
          refreshVettingEventsResults={refreshResults}
          refreshVettingEvents={refresh}
          organisationId={organisationId}
          isOpen={isComplianceModalOpened}
          setIsOpen={setIsComplianceModalOpened}
          vettingEventsResults={vettingEventsResults}
          vettingEvents={vettingEventsHistory}
        />
      )}
      {isEditComplianceModalOpened && (
        <EditComplianceModal
          isOpened={isEditComplianceModalOpened}
          setIsOpened={setIsEditComplianceModalOpened}
          vettingEventsResults={vettingEventsResults}
          refreshVettingEventsResults={refreshVettingEventsResults}
          refresh={refresh}
          organisationId={organisationId}
          organisationName={nonprofitName}
        />
      )}
      <Modal open={isResubmitFlagsModalOpened}>
        <ConfirmationModal
          title={t('confirmation.resubmitFlags.title')}
          loading={isGettingVettingEventsResultsLoading || isResubmittingFlags}
          primaryButtonText={t('button.resubmitFlags')}
          primaryBtnTestId="resubmit-flags"
          secondaryButtonText={t('button.cancel')}
          disabled={isResubmitFlagsModalDisabled}
          handleSubmit={() => getVettingEventsResultsFunc({ organisationId })}
          handleClose={() => setIsResubmitFlagsModalOpened(false)}
        >
          {t('confirmation.resubmitFlags.description', {
            name: nonprofitName
          })}
        </ConfirmationModal>
      </Modal>
    </Grid>
  )
}
